import React, { useState, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import './SmPCChanges.css';
import SmPCChangesGuide from './SmPCChangesGuide';

// Using standard symbols or a placeholder for the icon names
// If you use a real icon library (like Boxicons), you must import those components.
const IconChevronDown = () => <i className="bx bx-chevron-down" style={{ fontSize: '1.2em', align: 'center' }}></i>;
const IconChevronRight = () => <i className="bx bx-chevron-right" style={{ fontSize: '1.2em', align: 'center' }}></i>;

export default function SmPCChanges({ changesData }) {
  // HOOKS MUST BE CALLED UNCONDITIONALLY FIRST
  const [selectedSections, setSelectedSections] = useState([]);
  const [availableSections, setAvailableSections] = useState([]);
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(false);
  const [isComponentExpanded, setIsComponentExpanded] = useState(false);
  const [isSmPCGuideOpen, setIsSmPCGuideOpen] = useState(false);

  const hasMissingData = useMemo(() => {
    if (!changesData || !changesData.products) return false;

    // Use .some() for early exit as soon as one missing event is found
    return changesData.products.some(product =>
      product.timeline?.some(event => event.is_missing)
    );
  }, [changesData]);

  // Extract unique sections
  useEffect(() => {
    // Perform data check inside useEffect
    if (!changesData || !changesData.products) return;

    const sectionSet = new Set();
    changesData.products.forEach(prod => {
      prod.sections.forEach(sec => {
        sectionSet.add(sec.id);
        if (sec.subsections) {
          sec.subsections.forEach(sub => sectionSet.add(sub.id));
        }
      });
    });
    const sectionsToSkip = ['4', '5', '6'];

    const sectionSetArray = Array.from(sectionSet);

    // 1. Filter out the unwanted sections
    const filteredSections = sectionSetArray.filter(section =>
      !sectionsToSkip.includes(section)
    );

    // 2. Sort the remaining sections
    const sortedSections = filteredSections.sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );

    // 3. Update the state
    setAvailableSections(sortedSections);
    // To match the original behavior when the component is shown, we'll keep this:
    setSelectedSections(sortedSections);
  }, [changesData]);

  // EARLY RETURN NOW MOVED AFTER HOOKS
  if (!changesData || !changesData.products) return null;

  const toggleFilter = (secId) => {
    if (selectedSections.includes(secId)) {
      setSelectedSections(selectedSections.filter(id => id !== secId));
    } else {
      setSelectedSections([...selectedSections, secId]);
    }
  };

  const openSmPCGuide = (e) => {
    e.stopPropagation(); // Prevent the parent collapse handler from firing
    setIsSmPCGuideOpen(true);
  };

  const closeSmPCGuide = () => {
    setIsSmPCGuideOpen(false);
  };

  return (
    <div className="med-content-container">
      {/* Top-level collapsible header */}
      <div
        className="smpc-top-header"
        onClick={() => setIsComponentExpanded(!isComponentExpanded)}
      >
        <span className="toggle-icon">
          {isComponentExpanded ? <IconChevronDown /> : <IconChevronRight />}
        </span>
        <h1 className="med-header">SmPC text changes since authorisation (Experimental)</h1>
        {/* Explanation button */}
        <button
          className="med-primary-solid med-bx-button med-tour-button smpc-guide-button"
          onClick={openSmPCGuide}
        >
          <i className="bx bx-info-circle med-button-image"></i>
          Explanation
        </button>
      </div>

      {/* Only render content if component is expanded */}
      {isComponentExpanded && (
        <>
          {/* TIMELINE SECTION - Foldable Container */}
          <div className="smpc-timeline-wrapper-outer">
            <div
              className={`smpc-timeline-header ${isTimelineExpanded ? 'expanded' : ''}`}
              onClick={() => setIsTimelineExpanded(!isTimelineExpanded)}
            >
              <span className="toggle-icon">{isTimelineExpanded ? <IconChevronDown /> : <IconChevronRight />}</span>
              <h3>📅 SmPC section changes timeline</h3>
            </div>

            {isTimelineExpanded && (
              <div className="smpc-timeline-body">
                {/* Filters */}
                <div className="smpc-filters-container">
                  <h4>Filter by Sections:</h4>
                  <div className="smpc-filter-controls">
                    <button className="smpc-btn" onClick={() => setSelectedSections(availableSections)}>Select All</button>
                    <button className="smpc-btn" onClick={() => setSelectedSections([])}>Deselect All</button>
                  </div>
                  <div className="smpc-filter-grid">
                    {availableSections.map(secId => (
                      <div key={secId} className="smpc-filter-item">
                        <input
                          type="checkbox"
                          id={`filter_${secId}`}
                          checked={selectedSections.includes(secId)}
                          onChange={() => toggleFilter(secId)}
                        />
                        <label htmlFor={`filter_${secId}`}>{secId}</label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Legend */}
                <div className="smpc-legend">
                  <div className="legend-item"><div className="smpc-timeline-point point-start static-legend"></div> First SmPC</div>
                  <div className="legend-item"><div className="smpc-timeline-point point-change static-legend"></div> Text change</div>
                  <div className="legend-item"><div className="smpc-timeline-point point-no-change static-legend"></div> No text change</div>
                  {/* Conditionally render the 'Missing data' legend item */}
                  {hasMissingData && (
                    <div className="legend-item"><div className="smpc-timeline-point point-missing static-legend"></div> Missing data</div>
                  )}
                  <div className="legend-item"><div className="smpc-timeline-point point-end static-legend"></div> Last SmPC</div>
                </div>

                {/* Timelines */}
                <div className="smpc-timelines-list">
                  {changesData.products.map(product => (
                    <ProductTimelineRow
                      key={product.name}
                      product={product}
                      selectedSections={selectedSections}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* DETAILED CHANGES LIST */}
          <div className="med-info-detail-group" style={{ marginTop: '30px' }}>
            <div className="med-info-detail-group-title">
              <h2>SmPC text changes since authorisation (per presentation)</h2>
            </div>
            {changesData.products.map(product => (
              <ProductChangeDetails key={uuidv4()} product={product} />
            ))}
          </div>
        </>
      )}
      <SmPCChangesGuide
              isOpen={isSmPCGuideOpen}
              onClose={closeSmPCGuide}
            />
    </div>
  );
}

// --- Sub-components (Rest of the file remains the same) ---

function ProductTimelineRow({ product, selectedSections }) {
  // useMemo must be called unconditionally first
  const { startDate, endDate, totalDuration, summaryText, eventsWithPosition, yearsToRender } = useMemo(() => {
    // Conditional logic is safely inside the useMemo callback
    if (!product.timeline || product.timeline.length < 2) {
      return {
        startDate: null,
        endDate: null,
        totalDuration: 0,
        summaryText: 'Loading or no data.',
        eventsWithPosition: [],
        yearsToRender: []
      };
    }

    const start = new Date(product.timeline[0].date);
    const end = new Date(product.timeline[product.timeline.length - 1].date);
    const startMs = start.getTime();
    const endMs = end.getTime();
    const duration = endMs - startMs;

    let changesCount = 0;
    let noChangesCount = 0;

    // Calculate positions and status based on filtered sections
    const processedEvents = product.timeline.map((event, index) => {
      const eventMs = new Date(event.date).getTime();
      const position = duration > 0 ? ((eventMs - startMs) / duration) * 100 : 0;

      let statusClass = 'point-no-change';
      let isSignificant = false;

      if (event.is_missing) {
        statusClass = 'point-missing';
      } else if (index === 0) {
        statusClass = 'point-start';
      } else if (index === product.timeline.length - 1) {
        statusClass = 'point-end';
      } else {
        // Check intersection of event changes and selected sections
        const eventChangedSections = Object.keys(event.changes_map || {}).filter(k => event.changes_map[k]);
        const relevantChange = eventChangedSections.some(sec => selectedSections.includes(sec));

        if (relevantChange) {
          statusClass = 'point-change';
          changesCount++;
          isSignificant = true;
        } else {
          noChangesCount++;
        }
      }

      return {
        ...event,
        position,
        statusClass,
        isSignificant
      };
    });

    // Calculate Years for Axis
    const years = [];
    if (duration > 0) {
      const startYear = start.getFullYear();
      const endYear = end.getFullYear();
      for (let y = startYear; y <= endYear; y++) {
        const yearDate = new Date(`${y}-01-01`).getTime();
        if (yearDate >= startMs && yearDate <= endMs) {
          const pos = ((yearDate - startMs) / duration) * 100;
          years.push({ year: y, pos });
        }
      }
    }

    // Summary Text Generation
    const dateStr = `${start.toISOString().slice(0, 10)} → ${end.toISOString().slice(0, 10)}`;
    // const sectionStr = selectedSections.length === 0 ? '0 sections selected' : `${selectedSections.length} selected sections`;
    const summary = `${dateStr}\n• ${changesCount} changed`;

    return {
      startDate: start,
      endDate: end,
      totalDuration: duration,
      summaryText: summary,
      eventsWithPosition: processedEvents,
      yearsToRender: years
    };
  }, [product.timeline, selectedSections]);

  // Now, the conditional return can be safely placed after the hook call
  if (!product.timeline || product.timeline.length < 2) return null;

  return (
    <div className="smpc-timeline-row">
      <div className="smpc-timeline-label-col">
        <div className="smpc-prod-name">{product.name}</div>
        <div className="smpc-timeline-summary">{summaryText}</div>
      </div>
      <div className="smpc-timeline-bar-col">
        <div className="smpc-timeline-track-line">
          {/* Year Ticks */}
          {yearsToRender.map(y => (
            <div key={y.year} className="smpc-year-marker" style={{ left: `${y.pos}%` }}>
              <div className="tick"></div>
              <div className="year-label">{y.year}</div>
            </div>
          ))}

          {/* Event Points */}
          {eventsWithPosition.map((evt, idx) => (
            <div
              key={idx}
              className={`smpc-timeline-point ${evt.statusClass}`}
              style={{ left: `${evt.position}%` }}
              // Tooltip content via CSS attr() or basic title
              data-date={evt.date}
              title={`${evt.date}: ${evt.name} (${evt.isSignificant ? 'Changes detected' : 'No changes in selection'})`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ProductChangeDetails({ product }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div style={{ marginBottom: '15px' }}>
      <div
        className="smpc-accordion-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="smpc-header-title">
          {isExpanded ? <IconChevronDown /> : <IconChevronRight />} {product.name}
        </span>
        <span className="smpc-change-badge">{product.stats.total_changes} changes</span>
      </div>

      {isExpanded && (
        <div className="smpc-accordion-content-wrapper">
          {product.sections.map(section => (
            <SectionDetails key={section.id} section={section} />
          ))}
        </div>
      )}
    </div>
  );
}

function SectionDetails({ section }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasChanges = section.total_changes > 0;

  // LOGIC FROM PREVIOUS STEP: Determine if the initial history entry should be skipped.
  // Skip if: has_subsections is true, history length is 1, AND content is non-informative.
  const shouldSkipInitialEntry = useMemo(() => {
    // Condition 1: Must have subsections (or equivalent for a top-level section)
    if (!section.has_subsections && !section.subsections) return false;

    // Condition 2: Must have exactly one history entry
    if (!section.history || section.history.length !== 1) return false;

    const initialEntry = section.history[0];

    // Condition 3: The single entry must be 'initial' type with non-informative content
    const nonInformativeContent = initialEntry.content === "Not available" || initialEntry.content === null;

    return initialEntry.type === 'initial' && nonInformativeContent;
  }, [section.history, section.has_subsections, section.subsections]);

  // Recursively render subsections
  const renderSubsections = () => {
    if (!section.subsections) return null;
    return section.subsections.map(sub => (
      <div key={sub.id} style={{ marginLeft: '20px', marginTop: '10px' }}>
        <SectionDetails section={sub} />
      </div>
    ));
  };


  return (
    <div style={{ marginBottom: '8px' }}>
      <div
        className={`smpc-section-header ${hasChanges ? 'has-changes' : ''}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span>
          {isExpanded ? <IconChevronDown /> : <IconChevronRight />}
          <strong> {section.id}:</strong> {section.title}
        </span>
        <span className={`smpc-change-count ${hasChanges ? 'active' : ''}`}>
          {section.total_changes}
        </span>
      </div>

      {isExpanded && (
        <div className="smpc-diff-container">
          {section.history && section.history.map((entry, idx) => {
            if (entry.type === 'no-change') return null;

            // Check from the previous requirement: skip if it's the initial non-informative entry
            if (idx === 0 && entry.type === 'initial' && shouldSkipInitialEntry) {
              return null;
            }

            return (
              <div key={idx} className="smpc-diff-entry">
                <div className="smpc-diff-meta">
                  {entry.type === 'initial' ? '📄 Initial Content' : '🔄 Changed in'} {entry.filename} ({entry.date})
                </div>
                <div
                  className="diff-content"
                  dangerouslySetInnerHTML={{ __html: entry.diff_html || entry.content }}
                />
              </div>
            );
          })}
          {renderSubsections()}
        </div>
      )}
    </div>
  );
}